import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HotelDetailView } from "../components/HotelDetailView.js";
import { ApiClientError } from "../lib/api.js";
import {
  createHotelNote,
  createHotelTask,
  getHotelDetail,
  localInputToIso,
} from "../lib/hotels.js";

export function HotelDetailPage() {
  const { hotelId } = useParams();
  const queryClient = useQueryClient();
  const [noteContent, setNoteContent] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDueAt, setTaskDueAt] = useState("");
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ["hotels", hotelId, "detail"],
    enabled: Boolean(hotelId),
    queryFn: () => getHotelDetail(hotelId!),
  });

  const noteMutation = useMutation({
    mutationFn: (content: string) => createHotelNote(hotelId!, content),
    onSuccess: () => {
      setNoteContent("");
      setActionMessage("Notiz gespeichert.");
    },
  });

  const taskMutation = useMutation({
    mutationFn: () =>
      createHotelTask({
        hotel_id: hotelId!,
        title: taskTitle.trim(),
        due_at: localInputToIso(taskDueAt),
      }),
    onSuccess: () => {
      setTaskTitle("");
      setTaskDueAt("");
      setActionMessage("Task erstellt.");
      void queryClient.invalidateQueries({
        queryKey: ["hotels", hotelId, "detail"],
      });
    },
  });

  if (!hotelId) {
    return (
      <HotelDetailView
        state="error"
        errorMessage="Ungültige Hotel-ID."
        detail={null}
        noteContent=""
        taskTitle=""
        taskDueAt=""
        onNoteContentChange={() => undefined}
        onTaskTitleChange={() => undefined}
        onTaskDueAtChange={() => undefined}
        onSubmitNote={() => undefined}
        onSubmitTask={() => undefined}
      />
    );
  }

  const state = detailQuery.isLoading
    ? "loading"
    : detailQuery.isError || !detailQuery.data
      ? "error"
      : "ready";

  const actionError =
    noteMutation.error instanceof ApiClientError
      ? noteMutation.error.message
      : taskMutation.error instanceof ApiClientError
        ? taskMutation.error.message
        : noteMutation.error || taskMutation.error
          ? "Aktion fehlgeschlagen."
          : null;

  return (
    <HotelDetailView
      state={state}
      errorMessage={
        detailQuery.error instanceof ApiClientError
          ? detailQuery.error.message
          : detailQuery.error
            ? "Lead konnte nicht geladen werden."
            : null
      }
      detail={detailQuery.data ?? null}
      noteContent={noteContent}
      taskTitle={taskTitle}
      taskDueAt={taskDueAt}
      actionMessage={actionMessage}
      actionError={actionError}
      submittingNote={noteMutation.isPending}
      submittingTask={taskMutation.isPending}
      onNoteContentChange={(value) => {
        setActionMessage(null);
        setNoteContent(value);
      }}
      onTaskTitleChange={(value) => {
        setActionMessage(null);
        setTaskTitle(value);
      }}
      onTaskDueAtChange={setTaskDueAt}
      onSubmitNote={() => {
        if (!noteContent.trim()) return;
        noteMutation.mutate(noteContent.trim());
      }}
      onSubmitTask={() => {
        if (!taskTitle.trim()) return;
        taskMutation.mutate();
      }}
    />
  );
}
